import { Lightbulb, LightbulbOff, Loader2, Power, PowerOff, CircleCheck, CircleAlert } from 'lucide-react';
import { api } from '@udm/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { memo, useEffect, useState } from 'react';
import { ApiRequestError, request } from '../../api/client';
import { useTranslation } from 'react-i18next';
import { deviceStatic } from '../../realtime/deviceStatic';
import { useDeviceLive } from '../../realtime/hooks';
import type { DeviceLive } from '../../realtime/types';
import { formatDateTime, formatMetric, formatRelative } from '../../ui/format';
import { isTerminal, sendCommand, useCommands } from './commands';
import { correctionKey, CorrectPositionButton, useGpsFixAllowed } from './PositionCorrection';

/**
 * Live device building blocks shared by the map card and the device page. Each part subscribes to ONE device and
 * selects primitives, so a delta for this device re-renders only the parts whose values changed.
 */

/** Display order of known metrics; any other metric the device reports follows alphabetically. */
export const METRIC_ORDER = [
  'electrical.voltage_v',
  'electrical.current_a',
  'electrical.power_w',
  'electrical.power_factor',
  'electrical.energy_wh',
  'electrical.apparent_power_va',
  'electrical.reactive_power_var',
  'electrical.frequency_hz',
  'electrical.leakage_current_ma',
  'electrical.leakage_voltage_v',
  'device.temperature_c',
  'light.on_time_s',
  'light.fault_time_s',
  'environment.lux',
  'environment.illuminance_lux',
  'light.relay_closed',
  'light.photocell_segment',
  'light.switch_count',
  'light.switch_on_count',
  'light.switch_off_count',
  'device.tilt_deg',
  'device.tilt_warning',
  'network.signal',
  'network.signal_pct',
  'network.rsrp_dbm',
  'network.sinr_db',
  'network.rssi_dbm',
  'network.snr',
  'device.uptime_s',
  'network.connected_time_s',
];
const HIDDEN = new Set(['light.dimmer_pct']); // shown by the dial / slider instead

const metricKeys = (d: DeviceLive | undefined) =>
  Object.keys(d?.metrics ?? {})
    .sort()
    .join('|');

export function useMetricLabel() {
  const { t } = useTranslation();
  return (metric: string) => {
    const [group, name] = metric.split('.') as [string, string];
    return t(`catalog.metric.${group}.${name}` as 'catalog.metric.electrical.power_w', {
      defaultValue: metric,
    });
  };
}

const MetricValue = memo(function MetricValue({ id, metric }: { id: string; metric: string }) {
  const value = useDeviceLive(id, (d) => d?.metrics?.[metric]);
  return <>{formatMetric(metric, value)}</>;
});

/** "measured 3 min ago" for the telemetry heading: values come from the device's latest report. */
export function MeasuredAt({ id }: { id: string }) {
  const { t } = useTranslation();
  const at = useDeviceLive(id, (d) => d?.measuredAt);
  if (!at) return null;
  return (
    <span className="muted small" title={formatDateTime(at)}>
      {t('devices.live.measured', { when: formatRelative(at) })}
    </span>
  );
}

/** All metrics the device reports (known ones first), each cell live. */
export function DeviceMetricsGrid({ id, compact = false }: { id: string; compact?: boolean }) {
  const { t } = useTranslation();
  const label = useMetricLabel();
  const keys = useDeviceLive(id, metricKeys);
  const present = keys ? keys.split('|').filter((k) => !HIDDEN.has(k)) : [];
  const ordered = [
    ...METRIC_ORDER.filter((k) => present.includes(k)),
    ...present.filter((k) => !METRIC_ORDER.includes(k)),
  ];
  if (!ordered.length) return <p className="muted small">{t('devices.live.noMetrics')}</p>;
  return (
    <dl className={compact ? 'metric-list' : 'metric-grid'}>
      {ordered.map((m) => (
        <div key={m} className="metric-cell">
          <dt>{label(m)}</dt>
          <dd>
            <MetricValue id={id} metric={m} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Light output ring: dimmer level as an arc, bulb lit when the output is on. */
export function LightDial({ id }: { id: string }) {
  const { t } = useTranslation();
  const dimmer = useDeviceLive(id, (d) => d?.metrics?.['light.dimmer_pct']);
  const online = useDeviceLive(id, (d) => d?.presence === 'online');
  // reported light output, when the device states one (e.g. a node's status answer): "abnormal" is shown as such
  const abnormal = useDeviceLive(id, (d) => d?.reported?.output === 'abnormal');
  const known = dimmer !== undefined;
  const on = known && dimmer > 0;
  const r = 52;
  const c = 2 * Math.PI * r;
  const arc = 0.8; // 288° gauge, open at the bottom
  const frac = known ? Math.max(0, Math.min(100, dimmer)) / 100 : 0;
  return (
    <div className={`light-dial${on ? ' is-on' : ''}${online ? '' : ' is-stale'}`}>
      <svg viewBox="0 0 128 128" aria-hidden>
        <circle
          className="track"
          cx="64"
          cy="64"
          r={r}
          strokeDasharray={`${c * arc} ${c}`}
          transform="rotate(126 64 64)"
        />
        <circle
          className="level"
          cx="64"
          cy="64"
          r={r}
          strokeDasharray={`${c * arc * frac} ${c}`}
          transform="rotate(126 64 64)"
        />
      </svg>
      <div className="light-dial-center">
        {on ? <Lightbulb size={26} strokeWidth={1.6} /> : <LightbulbOff size={26} strokeWidth={1.6} />}
        <span className="light-dial-value">{known ? formatMetric('light.dimmer_pct', dimmer) : '—'}</span>
        <span className="light-dial-state">
          {abnormal
            ? t('deviceOps.outputAbnormal')
            : known
              ? t(on ? 'devices.live.on' : 'devices.live.off')
              : t('devices.live.unknownOutput')}
        </span>
      </div>
    </div>
  );
}

/** The rate-limit refusal's numbers for the message ("try again in N min"). */
const rateParams = (p: Record<string, unknown> | undefined) =>
  p && typeof p.retry_after_s === 'number'
    ? { max: p.max_actions, window: p.window_minutes, retry: Math.max(1, Math.ceil(p.retry_after_s / 60)) }
    : {};

function CommandStatusLine({ id }: { id: string }) {
  const { t } = useTranslation();
  const cmd = useCommands((s) => {
    const cid = s.lastByDevice[id];
    return cid ? s.byId[cid] : undefined;
  });
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    setVisible(true);
    if (!cmd || !isTerminal(cmd.status) || cmd.status !== 'SUCCESS') return;
    const h = setTimeout(() => setVisible(false), 6000);
    return () => clearTimeout(h);
  }, [cmd]);
  if (!cmd || !visible) return null;
  const [group, name] = cmd.capability.split('.') as [string, string];
  const what = t(`catalog.capability.${group}.${name}` as 'catalog.capability.device.reset', {
    defaultValue: cmd.capability,
  });
  const level = typeof cmd.params.level_pct === 'number' ? ` ${cmd.params.level_pct}%` : '';
  if (!isTerminal(cmd.status))
    return (
      <p className="cmd-status pending" role="status">
        <Loader2 size={14} className="spin" /> {t('devices.live.sending', { what: what + level })}
      </p>
    );
  if (cmd.status === 'SUCCESS')
    return (
      <p className="cmd-status ok" role="status">
        <CircleCheck size={14} /> {t('devices.live.done', { what: what + level })}
      </p>
    );
  const code = cmd.status === 'TIMEOUT' && !cmd.errorCode ? 'TIMEOUT' : (cmd.errorCode ?? cmd.status);
  return (
    <p className="cmd-status failed" role="alert">
      <CircleAlert size={14} /> {what + level}:{' '}
      {t(`devices.commandError.${code}` as 'devices.commandError.default', {
        ...rateParams(cmd.errorParams),
        defaultValue: t('devices.commandError.default', { code }),
      })}
    </p>
  );
}

/**
 * On / Off / dimmer controls, rendered from the device's capabilities (never from its type). Disabled while the
 * device is not online or a command for it is in flight (one command per device, as the gateway serializes them).
 */
export function DeviceControls({
  id,
  capabilities,
  canControl,
}: {
  id: string;
  capabilities: readonly string[];
  canControl: boolean;
}) {
  const { t } = useTranslation();
  const online = useDeviceLive(id, (d) => d?.presence === 'online');
  const presence = useDeviceLive(id, (d) => d?.presence);
  // A device reached through a concentrator can be commanded while the concentrator is online even if its own presence
  // is not confirmed yet (Central routes the command through the concentrator's connection); OFFLINE stays disabled.
  const upstream = deviceStatic.get(id)?.upstream;
  const upstreamOnline = useDeviceLive(upstream?.id ?? '', (d) => d?.presence === 'online');
  const viaUpstream = !online && presence !== 'offline' && Boolean(upstream) && upstreamOnline;
  const dimmer = useDeviceLive(id, (d) => d?.metrics?.['light.dimmer_pct']);
  const busy = useCommands((s) => {
    const cid = s.lastByDevice[id];
    const c = cid ? s.byId[cid] : undefined;
    return c !== undefined && !isTerminal(c.status);
  });
  const [level, setLevel] = useState<number>(dimmer && dimmer > 0 ? dimmer : 100);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!dragging && dimmer !== undefined && dimmer > 0) setLevel(dimmer);
  }, [dimmer, dragging]);

  const canOn = capabilities.includes('device.power_on');
  const canOff = capabilities.includes('device.power_off');
  const canDim = capabilities.includes('light.set_dimmer');
  if (!canOn && !canOff && !canDim) return null;
  if (!canControl) return <p className="muted small">{t('devices.live.noControl')}</p>;
  const disabled = !(online || viaUpstream) || busy;
  const on = dimmer !== undefined && dimmer > 0;
  const commit = () => {
    setDragging(false);
    if (level !== dimmer) void sendCommand(id, 'light.set_dimmer', { level_pct: level });
  };

  return (
    <div className="device-controls">
      {viaUpstream && (
        <p className="muted small" role="note">
          {t('deviceOps.viaUpstream', { name: upstream?.name ?? '' })}
        </p>
      )}
      {(canOn || canOff) && (
        <div className="power-toggle" role="group" aria-label={t('devices.live.output')}>
          {canOff && (
            <button
              type="button"
              aria-pressed={dimmer === 0}
              disabled={disabled}
              onClick={() => void sendCommand(id, 'device.power_off')}
            >
              <PowerOff size={15} /> {t('devices.live.off')}
            </button>
          )}
          {canOn && (
            <button
              type="button"
              aria-pressed={on}
              disabled={disabled}
              onClick={() => void sendCommand(id, 'device.power_on')}
            >
              <Power size={15} /> {t('devices.live.on')}
            </button>
          )}
        </div>
      )}
      {canDim && (
        <label className="dimmer-row">
          <span className="muted small">{t('devices.live.dimmer')}</span>
          <input
            type="range"
            min={1}
            max={100}
            step={1}
            value={level}
            disabled={disabled}
            style={{ '--fill': `${level}%` } as React.CSSProperties}
            onChange={(e) => {
              setDragging(true);
              setLevel(Number(e.target.value));
            }}
            onPointerUp={commit}
            onKeyUp={(e) => (e.key.startsWith('Arrow') || e.key === 'Home' || e.key === 'End') && commit()}
            aria-valuetext={`${level}%`}
          />
          <span className="dimmer-value">{level}%</span>
        </label>
      )}
      {!online && <p className="muted small">{t('devices.live.offlineHint')}</p>}
      <CommandStatusLine id={id} />
    </div>
  );
}

/** "Offline · no traffic · since 5 min" — the presence with its reason and change time (live). */
export function PresenceReason({ id }: { id: string }) {
  const { t } = useTranslation();
  const reason = useDeviceLive(id, (d) => d?.presenceReason);
  const since = useDeviceLive(id, (d) => d?.presenceChangedAt);
  if (!reason && !since) return null;
  return (
    <span className="muted small">
      {reason
        ? t(`presence.reason.${reason}` as 'presence.reason.connected', { defaultValue: reason })
        : null}
      {reason && since ? ' · ' : null}
      {since ? t('presence.since', { when: formatRelative(since) }) : null}
    </span>
  );
}

/** Presence facts kept separately (user decision 2026-09-25): state, reason, change time, connection, traffic. */
export function PresenceFacts({ id }: { id: string }) {
  const { t } = useTranslation();
  const reason = useDeviceLive(id, (d) => d?.presenceReason);
  const changed = useDeviceLive(id, (d) => d?.presenceChangedAt);
  const connected = useDeviceLive(id, (d) => d?.connectedSince);
  const seen = useDeviceLive(id, (d) => d?.lastSeenAt);
  return (
    <>
      <dt>{t('presence.why')}</dt>
      <dd>
        {reason
          ? t(`presence.reason.${reason}` as 'presence.reason.connected', { defaultValue: reason })
          : '—'}
      </dd>
      <dt>{t('presence.changedAt')}</dt>
      <dd title={changed ? formatDateTime(changed) : undefined}>{formatRelative(changed ?? undefined)}</dd>
      <dt>{t('presence.connectedSince')}</dt>
      <dd title={connected ? formatDateTime(connected) : undefined}>
        {connected ? formatRelative(connected) : '—'}
      </dd>
      <dt>{t('presence.lastTraffic')}</dt>
      <dd title={seen ? formatDateTime(seen) : undefined}>{formatRelative(seen)}</dd>
    </>
  );
}

/**
 * On-demand reads (like the legacy "Status Query" / "Data Query"): measurements now (the answer also updates the live
 * state) and the GPS position, compared with the position we have. Shown only for supported capabilities. "Check
 * GPS" and the position correction it leads to are the superadmin's only (U19); `correction` = false when the page
 * shows "Correct position…" elsewhere (next to "Show on map").
 */
export function DeviceQueries({
  id,
  capabilities,
  canControl,
  correction = true,
}: {
  id: string;
  capabilities: readonly string[];
  canControl: boolean;
  correction?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const gpsAllowed = useGpsFixAllowed();
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<{
    key: string;
    r: api.DeviceQueryResponse | null;
    error?: string;
  } | null>(null);
  const keys = ['telemetry.read', 'location.read'].filter(
    (k) => capabilities.includes(k) && (k !== 'location.read' || gpsAllowed),
  );
  if (!canControl || !keys.length) return null;
  const run = async (key: string) => {
    setBusy(key);
    setResult(null);
    try {
      const r = await request('POST', `/api/v1/devices/${id}/queries`, { query_key: key }, (v) =>
        api.DeviceQueryResponse.parse(v),
      );
      setResult({ key, r });
      if (key === 'location.read' && r.ok) void qc.invalidateQueries({ queryKey: correctionKey(id) });
    } catch (e) {
      setResult({ key, r: null, error: e instanceof ApiRequestError ? e.code : 'NETWORK_ERROR' });
    } finally {
      setBusy(null);
    }
  };
  const lat = result?.r?.result?.lat;
  const lon = result?.r?.result?.lon;
  return (
    <div className="device-queries">
      <div className="toolbar">
        {keys.map((k) => (
          <button key={k} type="button" className="btn" disabled={busy !== null} onClick={() => void run(k)}>
            {busy === k ? <Loader2 size={14} className="spin" /> : null}
            {t(`devices.query.${k === 'telemetry.read' ? 'readNow' : 'checkGps'}`)}
          </button>
        ))}
        {correction && keys.includes('location.read') && (
          <CorrectPositionButton id={id} name={deviceStatic.get(id)?.name ?? id} />
        )}
      </div>
      {result && (
        <p className={`cmd-status ${result.r?.ok ? 'ok' : 'failed'}`} role="status">
          {result.r?.ok ? <CircleCheck size={14} /> : <CircleAlert size={14} />}{' '}
          {result.r?.ok
            ? result.key === 'location.read' && typeof lat === 'number' && typeof lon === 'number'
              ? t('devices.query.gpsOk', {
                  lat: lat.toFixed(6),
                  lon: lon.toFixed(6),
                  distance:
                    typeof result.r.distance_m === 'number'
                      ? t('devices.query.distance', { m: result.r.distance_m })
                      : '',
                })
              : t('devices.query.readOk', { ms: result.r.duration_ms })
            : t(
                `devices.query.error.${result.r?.error_code ?? result.error ?? 'default'}` as 'devices.query.error.default',
                {
                  defaultValue: t('devices.query.error.default', {
                    code: result.r?.error_code ?? result.error,
                  }),
                },
              )}
        </p>
      )}
    </div>
  );
}
